export interface EncryptionKey {
  key: string;
  passphrase: string;
}

const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const base64ToUint8Array = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/**
 * Encrypts a value using AES-CBC with PBKDF2 key derivation.
 *
 * @param plainText - The value to encrypt
 * @param secretKey - The encryption key containing key and passphrase
 * @returns Base64 encoded string with IV prepended to ciphertext
 */
export const encryptValue = async (
  plainText: string,
  secretKey: EncryptionKey,
): Promise<string> => {
  if (!plainText) {
    return plainText;
  }

  // Validate that we have encryption keys
  if (!secretKey.key || !secretKey.passphrase) {
    console.warn('Encryption keys not configured, returning plain text');
    return plainText;
  }

  const encoder = new TextEncoder();

  const password = encoder.encode(secretKey.key);
  const salt = encoder.encode(secretKey.passphrase);

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    password,
    'PBKDF2',
    false,
    ['deriveBits'],
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100_000,
      hash: 'SHA-256',
    },
    keyMaterial,
    256,
  );

  const aesKey = await crypto.subtle.importKey(
    'raw',
    derivedBits,
    { name: 'AES-CBC' },
    false,
    ['encrypt'],
  );

  const iv = crypto.getRandomValues(new Uint8Array(16));

  const plainTextBytes = encoder.encode(plainText);
  const cipherText = await crypto.subtle.encrypt(
    {
      name: 'AES-CBC',
      iv: iv,
    },
    aesKey,
    plainTextBytes,
  );

  const cipherBytes = new Uint8Array(iv.length + cipherText.byteLength);
  cipherBytes.set(iv, 0);
  cipherBytes.set(new Uint8Array(cipherText), iv.length);

  return uint8ArrayToBase64(cipherBytes);
};

/**
 * Decrypts a value using AES-CBC with PBKDF2 key derivation.
 *
 * @param cipherText - The Base64 encoded string with IV prepended to ciphertext
 * @param secretKey - The encryption key containing key and passphrase
 * @returns The decrypted plain text string
 */
export const decryptValue = async (
  cipherText: string,
  secretKey: EncryptionKey,
): Promise<string> => {
  if (!cipherText) {
    return cipherText;
  }

  // Validate that we have encryption keys
  if (!secretKey.key || !secretKey.passphrase) {
    console.warn('Encryption keys not configured, returning cipher text');
    return cipherText;
  }

  try {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();

    const password = encoder.encode(secretKey.key);
    const salt = encoder.encode(secretKey.passphrase);

    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      password,
      'PBKDF2',
      false,
      ['deriveBits'],
    );

    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100_000,
        hash: 'SHA-256',
      },
      keyMaterial,
      256,
    );

    const aesKey = await crypto.subtle.importKey(
      'raw',
      derivedBits,
      { name: 'AES-CBC' },
      false,
      ['decrypt'],
    );

    const cipherBytes = base64ToUint8Array(cipherText);
    const iv = cipherBytes.slice(0, 16);
    const encryptedData = cipherBytes.slice(16);

    const decryptedData = await crypto.subtle.decrypt(
      {
        name: 'AES-CBC',
        iv: iv,
      },
      aesKey,
      encryptedData,
    );

    return decoder.decode(decryptedData);
  } catch (error) {
    console.warn('Decryption failed, returning original text:', error);
    return cipherText;
  }
};

/**
 * Encrypts the idNo field in an applicant object
 */
export const encryptApplicantIdNo = async <
  T extends { idNo: string; idType?: string },
>(
  applicant: T,
  secretKey: EncryptionKey,
): Promise<T> => {
  if (!applicant.idNo) {
    return applicant;
  }

  const encryptedIdNo = await encryptValue(applicant.idNo, secretKey);
  return {
    ...applicant,
    idNo: encryptedIdNo,
  };
};

/**
 * Encrypts idNo for all applicants in an array
 */
export const encryptApplicantsIdNo = async <
  T extends { idNo: string; idType?: string },
>(
  applicants: T[],
  secretKey: EncryptionKey,
): Promise<T[]> => {
  return Promise.all(
    applicants.map((applicant) => encryptApplicantIdNo(applicant, secretKey)),
  );
};
